// 모달 스택 — 여러 c6 Modal이 겹칠 때(예: 잠금 위 확인창) body 부수효과를 조율. per-modal로 처리하면 두
// 개가 동시에 뜰 때 엉킨다(아래 모달이 위 root를 inert, scroll lock 복원값 꼬임 등). depth-2를 안전하게:
//
// - scroll lock: ref-count. 첫 등록에서 body overflow 잠그고 원래 값 저장, 마지막 해제에서 복원.
// - inert: topmost 모달(최상위 z; 동률이면 나중 등록) 기준 z 낮거나 같은 body 자식만 inert. z 높은 비-Modal
//   오버레이(예약 1200 cover 등)는 보존해 모달 위에서 상호작용 가능. 우리가 건 inert만 추적 → 앱 inert 보존.
// - 늦게 추가되는 body 자식(toast/action portal 등)도 MutationObserver로 같은 규칙 적용 — 스냅샷만 하면
//   이후 추가된 sibling이 focusable로 남아 trap이 샌다.
//
// 3겹+도 z 순서로 동작하나 1차 보증은 depth-2. focus 조율은 Modal이 이 모듈의 topmost 질의로 수행.

// Modal Backdrop DOM 마커(contract) — Modal이 렌더한다. in-modal AnchoredPopup이 소속 모달을 찾을 때 사용.
export const MODAL_BACKDROP_ATTR = "data-c6-modal-backdrop";
// 모달이 exit(closing) 시작 시 Backdrop에 다는 마커. motion 있는 브라우저에선 모달이 MOTION_MS 동안 연결된 채
// fade-out하므로, in-modal popup은 el disconnect(unmount)가 아니라 이 신호로 즉시 self-close해야 그 사이
// interactive로 남지 않는다.
export const MODAL_EXITING_ATTR = "data-c6-modal-exiting";

// mountNode = 그 모달의 Dialog(`role="dialog" aria-modal`) — AnchoredPopup이 모달 안에서 열릴 때 그 portalEl을
// 여기에 append한다(append 대상이지 React portal 자체는 아니다 — 진짜 portal 대상은 AnchoredPopup의 portalEl).
type ModalStackEntry = { el: HTMLElement; z: number; mountNode: HTMLElement | null };

const stack: ModalStackEntry[] = [];
// 우리가 inert 건 요소만 추적 — 앱이 직접 건 inert를 cleanup에서 안 지우기 위함.
const inertedByUs = new Set<HTMLElement>();
let savedBodyOverflow: string | null = null;
let observer: MutationObserver | null = null;
// 스택 전체가 닫힐 때 복원할 "원래 opener"(첫 모달을 연 페이지 요소). 각 모달의 자체 restore 타깃은
// close-all에서 먼저 제거된 하위 모달 내부를 가리킬 수 있어 신뢰 불가.
let baseRestore: HTMLElement | null = null;

function resolveTopmostEntry(): ModalStackEntry | null {
  if (stack.length === 0) return null;
  let top = stack[0];
  // z 크면 위. 동률이면 나중 등록(스택 뒤쪽)이 위.
  for (const entry of stack) {
    if (entry.z >= top.z) top = entry;
  }
  return top;
}

function resolveTopmost(): HTMLElement | null {
  return resolveTopmostEntry()?.el ?? null;
}

// body 직속 자식의 유효 z. 등록된 모달 root는 스택 z(컨테이너는 z:auto, backdrop이 z 보유), 그 외는
// computed z-index. auto는 0.
function effectiveZ(child: HTMLElement): number {
  const entry = stack.find((e) => e.el === child);
  if (entry) return entry.z;
  const css = getComputedStyle(child).zIndex;
  return css === "auto" ? 0 : Number.parseInt(css, 10) || 0;
}

// topmost 모달보다 z 낮거나 같은 body 직속 자식을 inert. topmost 자신과 z 높은 비-Modal 오버레이(예약
// 1200 cover 등)는 보존. 대상에서 빠진 것은 우리가 건 inert를 해제.
function applyInert(): void {
  const top = resolveTopmostEntry();
  const shouldBeInert = new Set<HTMLElement>();
  for (const child of Array.from(document.body.children)) {
    if (!(child instanceof HTMLElement) || !top || child === top.el) continue;
    if (effectiveZ(child) > top.z) continue;
    shouldBeInert.add(child);
  }

  // 우리가 걸었지만 더 이상 대상 아닌 것 복원.
  for (const el of inertedByUs) {
    if (!shouldBeInert.has(el)) {
      el.removeAttribute("inert");
      inertedByUs.delete(el);
    }
  }
  // 새로 inert (앱이 이미 inert한 건 안 건드림).
  for (const el of shouldBeInert) {
    if (inertedByUs.has(el) || el.hasAttribute("inert")) continue;
    el.setAttribute("inert", "");
    inertedByUs.add(el);
  }
}

function clearAllInert(): void {
  for (const el of inertedByUs) el.removeAttribute("inert");
  inertedByUs.clear();
}

/**
 * `el`의 우리가 건 inert를 해제 + 추적(inertedByUs)에서 제거. AnchoredPopup이 popup 노드를 body→Dialog로
 * 옮길 때 사용 — observer는 "추가"에만 반응해 body에서 빠진 노드를 자동 해제 안 하므로 명시 해제 필요. raw
 * `removeAttribute`와 달리 추적 set까지 정리해 다음 applyInert와의 desync 방지. 우리가 안 건 inert는 보존.
 */
export function releaseInert(el: HTMLElement): void {
  if (!inertedByUs.has(el)) return;
  el.removeAttribute("inert");
  inertedByUs.delete(el);
}

/** 현재 최상위 모달의 portal container. 없으면 null. (focus 조율용) */
export function topmostModalEl(): HTMLElement | null {
  return resolveTopmost();
}

/** 모달이 자기 Dialog를 popup mount node로 등록. Dialog 마운트 뒤 호출 — 엔트리에서 바로 읽기 위함. */
export function setModalMountNode(el: HTMLElement, node: HTMLElement | null): void {
  const entry = stack.find((e) => e.el === el);
  if (entry) entry.mountNode = node;
}

/**
 * topmost 모달의 popup mount node = 그 모달의 Dialog(`role="dialog" aria-modal`). 없으면 null. AnchoredPopup의
 * portalEl을 여기에 append하면 콘텐츠 위로 그려지고, Backdrop 아닌 Dialog라 aria-modal 서브트리에 포함(보조기술 인식).
 * Dialog는 position:relative + opacity(transform/filter 없음)라 안의 position:fixed Panel이 viewport 기준 유지.
 */
export function topmostModalMountNode(): HTMLElement | null {
  return resolveTopmostEntry()?.mountNode ?? null;
}

/** 특정 모달 `el`의 mount node. relocate가 자기를 띄운 모달(topmost 아닐 수도)의 Dialog로 옮길 때 사용. */
export function modalMountNode(el: HTMLElement): HTMLElement | null {
  return stack.find((e) => e.el === el)?.mountNode ?? null;
}

/** `el`이 현재 최상위 모달인가. (열릴 때 포커스를 가로챌지 판단) */
export function isTopmostModal(el: HTMLElement): boolean {
  return resolveTopmost() === el;
}

/** 스택 전체가 닫힐 때 포커스를 되돌릴 원래 opener(첫 모달을 연 페이지 요소). 없으면 null. */
export function baseRestoreTarget(): HTMLElement | null {
  return baseRestore;
}

/**
 * 등록된 모달의 z 갱신 — `level`이 open 중 바뀔 때. 등록 시점 z로 굳으면 시각상 앞인데 우선순위는 낮게
 * 취급되는 역전이 생긴다. z 변경 후 inert 재계산. focus 이동은 호출자(Modal)가 판단.
 */
export function setModalLevel(el: HTMLElement, z: number): void {
  const entry = stack.find((e) => e.el === el);
  if (!entry || entry.z === z) return;
  entry.z = z;
  applyInert();
}

/**
 * 모달 portal container를 스택에 등록. scroll lock(ref-count) + 바깥 inert를 조율하고 해제 함수를 반환.
 * `el`은 호출 전 이미 `document.body`에 append돼 있어야 함(없으면 inert 계산에서 누락).
 * `restoreTarget`은 이 모달을 연 직전 포커스 — 첫 모달일 때만 close-all용 baseRestore로 보존.
 */
export function registerModal(
  el: HTMLElement,
  z: number,
  restoreTarget: HTMLElement | null,
): () => void {
  if (stack.length === 0) {
    baseRestore = restoreTarget;
    savedBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    observer = new MutationObserver((mutations) => {
      const added = mutations.some((m) => m.addedNodes.length > 0);
      if (added) applyInert();
    });
    observer.observe(document.body, { childList: true });
  }

  stack.push({ el, z, mountNode: null });
  applyInert();

  return function unregister() {
    const index = stack.findIndex((entry) => entry.el === el);
    if (index !== -1) stack.splice(index, 1);

    if (stack.length === 0) {
      observer?.disconnect();
      observer = null;
      clearAllInert();
      if (savedBodyOverflow !== null) document.body.style.overflow = savedBodyOverflow;
      savedBodyOverflow = null;
    } else {
      applyInert();
    }
  };
}
